import React from 'react';
import { ArrowRight, Heart, Music, Trophy, Coffee, Dog, Sparkles, MessageCircle, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const Home = () => {
  return (
    <div className="min-h-screen bg-black text-white">

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black opacity-90"></div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          {/* Hero Image */}
          <div className="mb-12">
            <div className="w-64 h-64 mx-auto rounded-full overflow-hidden border-4 border-white/10 shadow-2xl">
              <img
                src="/gallery/hero-main.png"
                alt="Layton Pace"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Better in person.<br />
            <span className="text-gray-400">Still worth a look.</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-4xl mx-auto leading-relaxed">
            I&rsquo;m Layton.
          </p>

          <p className="text-lg md:text-xl text-gray-300 mb-4 max-w-4xl mx-auto leading-relaxed">
            I live in Northwest Arkansas, and I&rsquo;m looking for something serious with someone funny,
            warm, easy to talk to, and not scared to joke back.
          </p>

          <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
            I&rsquo;m direct, loyal, a little sarcastic once I&rsquo;m comfortable, and probably better at making
            you laugh than writing the perfect bio.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href="#about">
              <Button data-testid="hero-get-to-know-btn" className="bg-white text-black hover:bg-gray-200 text-lg px-8 py-6">
                Get to Know Me
              </Button>
            </a>
            <a href="#connect">
              <Button data-testid="hero-text-me-btn" variant="outline" className="border-white text-white hover:bg-white hover:text-black text-lg px-8 py-6">
                Text Me
              </Button>
            </a>
          </div>

          <p className="text-gray-400 text-sm italic">
            Dating apps got the short version. This is the slightly better one.
          </p>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section id="about" className="py-24 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
            So who is Layton?
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="/gallery/IMG_3809.jpg"
                alt="Layton casual"
                className="w-full h-auto rounded-lg shadow-2xl"
              />
            </div>

            <div className="order-1 lg:order-2 text-lg md:text-xl text-gray-300 space-y-6 leading-relaxed">
              <p>
                I&rsquo;m not trying to be everybody&rsquo;s type. I&rsquo;d rather be the right fit for one person.
              </p>
              <p>
                I&rsquo;m pretty easygoing, but I know what I want. I like people who are real, funny,
                thoughtful, and not constantly trying to make simple things complicated.
              </p>
              <p>
                Outside of dating, I&rsquo;m usually happiest when there is something to look forward to.
                Concerts, sports, patio weather, or hanging at my usual spots around town. I like
                getting out, being around a good atmosphere, and making normal plans feel a little
                more fun than they probably should.
              </p>
              <p>
                Career-wise, I work in transportation, so I spend a lot of my day solving problems,
                keeping things moving, and dealing with people who probably needed the answer five
                minutes ago. It fits me pretty well because I&rsquo;m detail-oriented, direct, and usually
                calm when things get messy.
              </p>
              <p>
                I have muscular dystrophy and use a wheelchair, so my life does look different in
                some ways. I&rsquo;m upfront about that because I would rather be honest from the start.
                I&rsquo;m not looking for someone to be my caregiver. I&rsquo;m looking for someone to actually
                get to know me.
              </p>
              <p>
                The short version: I like laughing, I like good conversation, I care about the people
                close to me, and if I like you, you will probably know.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK HITS SECTION */}
      <section className="py-24 px-4 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">
            A quick read on me
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Direct', desc: 'I would rather be clear than make someone guess.' },
              { title: 'Playful', desc: "If I'm giving you a hard time, there is a good chance I like you." },
              { title: 'Loyal', desc: 'I do not half-show up for people I care about.' },
              { title: 'Dry humor', desc: 'Sometimes my face says the joke before I do.' },
              { title: 'Family-minded', desc: 'The people close to me matter a lot.' },
              { title: 'Steady', desc: 'I like fun. I do not need drama to feel something.' }
            ].map((trait, idx) => (
              <div key={idx} className="bg-black/50 p-8 rounded-lg border border-gray-800 hover:border-gray-600 transition-all">
                <h3 className="text-2xl font-bold mb-4">{trait.title}</h3>
                <p className="text-gray-400 leading-relaxed">{trait.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT I'M INTO SECTION */}
      <section className="py-24 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center">
            My kind of vibe
          </h2>

          <p className="text-xl text-gray-400 mb-12 text-center">
            A few things that usually make life better:
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-12">
            <div>
              <img
                src="/gallery/IMG_4021.jpg"
                alt="Concert life"
                className="w-full h-auto rounded-lg shadow-2xl"
              />
              <p className="text-center text-gray-500 mt-4 text-sm italic">Living for the music</p>
            </div>

            <div className="space-y-8">
              {[
                { icon: <Music className="w-8 h-8" />, title: 'Live music', desc: 'A good concert can fix a lot.' },
                { icon: <Trophy className="w-8 h-8" />, title: 'Sports', desc: "Razorbacks, Chiefs, and the occasional strong opinion I will defend like I'm on the coaching staff." },
                { icon: <Coffee className="w-8 h-8" />, title: 'Good food', desc: "I'm always up for a place with flavor, personality, and a reason to stay a little longer." },
                { icon: <Dog className="w-8 h-8" />, title: 'Dogs', desc: 'Sydney and Scout are cute, spoiled, and fully aware of it.' },
                { icon: <Sparkles className="w-8 h-8" />, title: 'Patio nights', desc: 'Warm weather, something good to drink, and no reason to rush.' },
                { icon: <MapPin className="w-8 h-8" />, title: 'Usual spots around town', desc: 'I like finding places that feel easy. The kind where you can relax, talk, laugh, and not feel like you have to make the night bigger than it needs to be.' }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-6 items-start">
                  <div className="text-white flex-shrink-0">{item.icon}</div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-lg leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DATING ME SECTION */}
      <section className="py-24 px-4 bg-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
            What dating me actually feels like
          </h2>

          <div className="text-lg md:text-xl text-gray-300 space-y-6 leading-relaxed">
            <p>
              It probably feels like laughing at something dumb, getting a text that is half sweet
              and half smart mouth, and realizing I pay attention more than I let on.
            </p>
            <p>
              I&rsquo;m not into acting uninterested so someone tries harder. If I like you, I&rsquo;m going to
              show it.
            </p>
            <p>
              I like flirting, joking around, checking in, and making plans that actually happen. I
              do not need constant attention, but I do like knowing the interest is mutual.
            </p>
            <p>
              I want dating to feel fun, not like a puzzle nobody asked to solve.
            </p>
            <p>
              The best kind of relationship, to me, is where we can mess with each other, be honest
              with each other, and still feel like we are on the same team.
            </p>
          </div>

          <div className="mt-12 p-8 bg-gray-900 rounded-lg border-l-4 border-white">
            <p className="text-2xl italic text-gray-200">
              &ldquo;Sweet when it matters. Smart mouth when it doesn&rsquo;t.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* WHAT I VALUE SECTION */}
      <section className="py-24 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">
            Things that matter to me
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Communication', desc: 'Say what you mean. It saves everybody time.' },
              { title: 'Consistency', desc: 'Effort should not feel like a guessing game.' },
              { title: 'Humor', desc: 'If we cannot laugh together, something is missing.' },
              { title: 'Attraction', desc: 'I want the emotional side and the physical side to both be there.' },
              { title: 'Trust', desc: "I like knowing we have each other's back." },
              { title: 'Effort', desc: 'The little things count when they are genuine.' }
            ].map((value, idx) => (
              <div key={idx} className="bg-black/50 p-6 rounded-lg border border-gray-800">
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-gray-400">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT I'M LOOKING FOR SECTION */}
      <section className="py-24 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
            The kind of woman I&rsquo;m drawn to
          </h2>

          <div className="text-lg md:text-xl text-gray-300 space-y-6 leading-relaxed mb-12">
            <p>
              I&rsquo;m drawn to a woman who is funny, warm, emotionally mature, and comfortable
              being herself.
            </p>
            <p>
              Someone with feminine energy, a little wit, and the ability to communicate without
              turning every conversation into a mystery.
            </p>
            <p>
              I like someone who can be sweet but still have personality. Confident but not cold.
              Serious about finding the right person, but still fun while getting there.
            </p>
            <p>
              Being comfortable with disability matters. Not because you have to know everything
              right away, but because I want someone open-minded enough to see the whole person.
            </p>
            <p>
              Mostly, I want someone who makes life feel lighter, not harder.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {['funny', 'warm', 'feminine energy', 'affectionate', 'emotionally mature', 'witty', 'communicative', 'relationship-minded', 'comfortable with disability'].map((tag, idx) => (
              <span key={idx} className="bg-white/10 px-6 py-2 rounded-full text-gray-300 border border-gray-700">
                {tag}
              </span>
            ))}
          </div>

          <p className="text-lg text-gray-400 text-center italic">
            If you can joke back, say what you mean, and make ordinary moments more fun, we&rsquo;ll
            probably get along.
          </p>
        </div>
      </section>

      {/* WHY THE SITE EXISTS SECTION */}
      <section className="py-24 px-4 bg-black">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            Why a website?
          </h2>

          <div className="text-lg md:text-xl text-gray-300 space-y-6 leading-relaxed">
            <p>
              Because apps make everyone sound the same after a while.
            </p>
            <p>
              A few photos and prompts can only do so much. This gives you a better feel for who I
              am, how I think, and whether I might actually be your kind of guy.
            </p>
            <p>
              Also, having a dating website is either a green flag or a very committed bit.
            </p>
            <p className="text-xl">
              I&rsquo;m choosing green flag.
            </p>
          </div>
        </div>
      </section>

      {/* FAST FACTS SECTION */}
      <section className="py-24 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
            A few fast facts
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-4">
              {[
                'I live in Northwest Arkansas.',
                'I am better in person than in a bio.',
                'I like a woman who can joke back.',
                'I appreciate honesty more than perfectly worded answers.',
                'I am usually more sarcastic than I look.',
                'I care a lot once I care.',
                'I do not need perfect, but I do need mature.',
                'I am a little sentimental, but I may deny that later.',
                'I would rather build one solid relationship than collect options.',
                'If you made it this far, I respect the commitment.'
              ].map((fact, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4">
                  <div className="w-2 h-2 bg-white rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-lg text-gray-300">{fact}</p>
                </div>
              ))}
            </div>

            <div>
              <img
                src="/gallery/concert-friends.png"
                alt="Good times with friends"
                className="w-full h-auto rounded-lg shadow-2xl"
              />
              <p className="text-center text-gray-500 mt-4 text-sm italic">Making memories with good people</p>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA SECTION */}
      <section className="py-24 px-4 bg-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            The right connection should feel natural
          </h2>

          <div className="text-lg md:text-xl text-gray-300 space-y-6 leading-relaxed mb-12">
            <p>I&rsquo;m not looking for perfect.</p>
            <p>
              I&rsquo;m looking for someone I can be comfortable with, attracted to, honest with,
              and excited to keep learning.
            </p>
            <p>
              Someone who makes things feel easy in the right way. Not boring. Not forced. Not
              confusing.
            </p>
            <p>
              Just two people who actually like each other and want to see where it can go.
            </p>
            <p className="italic text-gray-400">
              If you are still reading and smiling a little, that is probably your sign.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#connect">
              <Button data-testid="closing-text-me-btn" className="bg-white text-black hover:bg-gray-200 text-lg px-8 py-6">
                Text Me
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href="#connect">
              <Button data-testid="closing-say-hi-btn" variant="outline" className="border-white text-white hover:bg-white hover:text-black text-lg px-8 py-6">
                Say Hi
                <MessageCircle className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CONNECT SECTION */}
      <section id="connect" className="py-24 px-4 bg-black">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center">
            Let&rsquo;s connect
          </h2>

          <p className="text-xl text-gray-400 mb-4 text-center">
            No form. No app required.
          </p>

          <p className="text-gray-500 text-center mb-12">
            If you made it this far, you might as well text me.
          </p>

          <div className="space-y-4" data-testid="contact-links">
            <a
              href="tel:+14794227742"
              data-testid="contact-phone-link"
              className="group flex items-center justify-between gap-4 px-6 py-5 bg-zinc-950 border border-zinc-800 hover:border-white hover:bg-zinc-900 transition-colors"
            >
              <div className="flex items-center gap-5 min-w-0">
                <div className="w-12 h-12 flex items-center justify-center border border-zinc-700 group-hover:border-white shrink-0 transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-500">
                    Call or text
                  </div>
                  <div className="text-lg md:text-xl font-medium mt-1 truncate">
                    (479) 422-7742
                  </div>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all shrink-0" />
            </a>

            <a
              href="mailto:wheelslp@gmail.com"
              data-testid="contact-email-link"
              className="group flex items-center justify-between gap-4 px-6 py-5 bg-zinc-950 border border-zinc-800 hover:border-white hover:bg-zinc-900 transition-colors"
            >
              <div className="flex items-center gap-5 min-w-0">
                <div className="w-12 h-12 flex items-center justify-center border border-zinc-700 group-hover:border-white shrink-0 transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-500">
                    Email
                  </div>
                  <div className="text-lg md:text-xl font-medium mt-1 truncate">
                    wheelslp@gmail.com
                  </div>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all shrink-0" />
            </a>
          </div>

          <p className="text-gray-500 text-center mt-12 text-sm italic">
            If you are smiling, that is probably a good sign.
          </p>
        </div>
      </section>

      {/* Footer Microcopy */}
      <div className="py-8 px-4 bg-black border-t border-gray-900">
        <p className="text-center text-gray-500 text-sm italic">
          Made for the woman who actually reads the whole thing.
        </p>
      </div>
    </div>
  );
};

export default Home;
